/**
 * ECharts Option Builders — shared utility functions.
 *
 * Pure helpers for column lookup, axis formatting, text measurement,
 * and auto-rotation.  No chart-type knowledge — used by all builders.
 */
import { formatChartValue, formatDate, isEpochTimestamp } from '../../../../utils/formatters';
import { FONT_FAMILY } from '../theme';
import type { ChartData } from '../../../../api/types';

// ── Column helpers ─────────────────────────────────────────────────────────

/** Get column index by name */
export function colIdx(data: ChartData, name: string): number {
  return data.columns.findIndex((c) => c.name === name);
}

/** Get column type by name */
export function colType(data: ChartData, name: string): string {
  const col = data.columns.find((c) => c.name === name);
  return col?.type ?? 'string';
}

/** Extract unique values from a column, preserving order */
export function uniqueValues(data: ChartData, colName: string): any[] {
  const idx = colIdx(data, colName);
  if (idx < 0) return [];
  const seen = new Set<any>();
  const result: any[] = [];
  for (const row of data.rows) {
    const v = row[idx];
    if (!seen.has(v)) {
      seen.add(v);
      result.push(v);
    }
  }
  return result;
}

// ── Axis formatting ────────────────────────────────────────────────────────

/** Format axis label — dates get formatted, others pass through */
export function formatAxisLabel(value: any, type: string): string {
  if (type === 'date' || (type === 'number' && isEpochTimestamp(value))) {
    return formatDate(value, 'date');
  }
  if (type === 'number') {
    return formatChartValue(value);
  }
  return String(value ?? '');
}

/** Build axis label formatter function */
export function axisLabelFormatter(type: string) {
  return (value: any) => formatAxisLabel(value, type);
}

// ── Text measurement ───────────────────────────────────────────────────────

/** Lazily-created offscreen canvas context for measuring text width */
let _measureCtx: CanvasRenderingContext2D | null = null;

const AXIS_FONT = `12px ${FONT_FAMILY}`;

/**
 * Measure the pixel width of a string using the chart axis font.
 * Uses an offscreen canvas (created once, never attached to DOM).
 */
export function measureText(text: string, font?: string): number {
  if (!_measureCtx) {
    const canvas = document.createElement('canvas');
    _measureCtx = canvas.getContext('2d')!;
  }
  _measureCtx.font = font || AXIS_FONT;
  return _measureCtx.measureText(text).width;
}

/**
 * Find the pixel width of the widest label in a list.
 */
export function maxLabelWidth(labels: string[], font?: string): number {
  if (!labels.length) return 0;
  return Math.max(...labels.map((l) => measureText(l, font)));
}

/**
 * Auto-rotate x-axis labels based on whether they fit horizontally.
 *
 * Measures the widest label and compares against the per-label slot
 * width (containerWidth / categoryCount). If labels don't fit → 45°.
 *
 * @param categories  Formatted category label strings
 * @param chartWidth  Available chart plot width in pixels (from container)
 */
export function autoRotate(categories: string[], chartWidth: number): number {
  if (categories.length <= 1) return 0;

  const widest = maxLabelWidth(categories);

  // Short labels (≤ 40px — numbers, abbreviations, short words) never need
  // rotation. ECharts' hideOverlap will auto-skip labels that don't fit,
  // which looks better than rotating tiny text at 45°.
  if (widest <= 40) return 0;

  // For longer labels, compare against available slot width
  const plotWidth = Math.max(chartWidth - 128, 200);
  const slotWidth = plotWidth / categories.length;

  if (widest + 8 <= slotWidth) return 0;
  return 45;
}

/**
 * Estimate the y-axis nameGap so the axis title doesn't overlap tick labels.
 *
 * Measures the widest formatted y-axis tick label and adds padding.
 */
export function estimateYAxisNameGap(seriesValues: Map<string, (number | null)[]>): number {
  let maxAbs = 0;
  for (const values of seriesValues.values()) {
    for (const v of values) {
      if (v !== null && Math.abs(v) > maxAbs) maxAbs = Math.abs(v);
    }
  }
  // ECharts auto-scales axis ticks and may produce labels wider than the
  // data max (e.g. data max 500M → tick at 1,000,000,000).  Round up to
  // the next power-of-10 so we measure the widest plausible tick label.
  const ceil = maxAbs > 0 ? Math.pow(10, Math.ceil(Math.log10(maxAbs))) : 0;
  const formatted = formatChartValue(ceil);
  return measureText(formatted) + 20;
}

/** Check if a column holds date-like data */
export function isDateColumn(data: ChartData, colName: string): boolean {
  const t = colType(data, colName);
  if (t === 'date') return true;
  if (t === 'number' && data.rows.length > 0) {
    const idx = colIdx(data, colName);
    return idx >= 0 && isEpochTimestamp(data.rows[0][idx]);
  }
  return false;
}